import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo } from 'react';

import { useMixpanel } from '../../../../../hooks';
import {
  REIMBURSEMENT_METHOD_BY_NAME,
  REIMBURSEMENT_METHODS,
} from '../../../../../constants';
import { EV59 } from '../../../../../services/mixpanel';
import { reimbursementMethodUpdateReset } from '../../../../../actions';

/**
 * Custom hook to log update reimbursement method event into mixpanel
 * every time the user updates their reimbursement method.
 */
const useUpdateReimbursementMethodEvent = ({ policyNumber, source }) => {
  const dispatch = useDispatch();
  const store = useSelector(({ billingPayments }) => ({
    billingPayments,
  }), shallowEqual);

  const {
    billingPayments: {
      reimbursementMethodUpdateSuccess,
      reimbursementMethodUpdated,
      reimbursementData,
    },
  } = store;

  const { sendEvent } = useMixpanel();

  const backupMethod = useMemo(() => {
    switch (reimbursementData.secondaryMethod.Type) {
      // only direct deposit or check is allowed to set as backup method
      // because of business rules
      case REIMBURSEMENT_METHODS.check:
        return REIMBURSEMENT_METHOD_BY_NAME.check;

      case REIMBURSEMENT_METHODS.deposit:
        return REIMBURSEMENT_METHOD_BY_NAME.directDeposit;

      default:
        return null;
    }
  }, [reimbursementData]);

  const reimbursementMethodText = useMemo(() => {
    const reimbursementType = reimbursementData.Type;

    switch (reimbursementType) {
      case REIMBURSEMENT_METHODS.check:
        return REIMBURSEMENT_METHOD_BY_NAME.check;

      case REIMBURSEMENT_METHODS.deposit:
        return REIMBURSEMENT_METHOD_BY_NAME.directDeposit;

      case REIMBURSEMENT_METHODS.careCredit:
        return REIMBURSEMENT_METHOD_BY_NAME.careCredit;

      default:
        return `unknown type ${reimbursementType}`;
    }
  }, [reimbursementData]);

  useEffect(() => {
    if (reimbursementMethodUpdated) {
      const data = {
        'backup reimbursement method': backupMethod,
        'policy brand': 'figo',
        'policy number': policyNumber,
        'reimbursement method type': reimbursementMethodText,
        source,
        status: reimbursementMethodUpdateSuccess ? 'success' : 'failed',
      };
      sendEvent(EV59({ data }));

      dispatch(reimbursementMethodUpdateReset());
    }
  }, [
    backupMethod,
    reimbursementMethodText,
    reimbursementMethodUpdated,
    reimbursementMethodUpdateSuccess,
  ]);
};

export { useUpdateReimbursementMethodEvent };
